import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, FileSignature, Camera, Scale, Search } from "lucide-react";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import SocialProofBar from "../components/SocialProofBar";
import FeatureCardGrid from "../components/FeatureCardGrid";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";
import { CITY_AGENTS, CITIES, initials } from "../lib/agents";

export default function ForTenants() {
  const { t } = useI18n();
  const [city, setCity]             = useState("Bangalore");
  const [citySearch, setCitySearch]   = useState("Bangalore");
  const [showCities, setShowCities]   = useState(false);

  const features = [
    {
      icon: "Lock",
      title: t("deposit_in_escrow"),
      description: "Your money is held safely with Orello — not in the landlord's account. They can't touch it without your sign-off.",
    },
    {
      icon: "Camera",
      title: t("proof_locked"),
      description: "Photos, videos, and condition notes sealed with a timestamp at move-in. No one can say it was like that later.",
    },
    {
      icon: "FileSignature",
      title: t("you_approve"),
      description: "Review each claim before a paisa moves. Dispute anything you disagree with.",
    },
    {
      icon: "Scale",
      title: t("disputes_resolved"),
      description: "ORE-AI — Orello's purpose-built dispute engine — reviews check-in photos, checkout evidence, and local market rates. Instant verdict. No courts, no lawyers.",
    },
  ];

  const tiles = [
    {
      icon: "📸",
      headline: "Move-in photos. Locked forever.",
      subtext: "Timestamped on day one. No one can edit or delete them — not the landlord, not us.",
    },
    {
      icon: "✅",
      headline: "You approve every deduction.",
      subtext: "Not a single rupee moves without your review and sign-off. Dispute anything you disagree with.",
    },
    {
      icon: "⚖️",
      headline: "ORE-AI instant verdict.",
      subtext: "India's first AI built for rental disputes — reviews photos, notes, and local market rates. Binding. Evidence-based. No courts.",
    },
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero */}
      <PageHero
        eyebrow={t("nav_for_tenants")}
        title={t("your_deposit_deserves")}
        subtitle="No more guessing. No more disappearing deposits. Orello holds your deposit securely — not in the landlord's account. Released only when both sides agree."
        cta1Text="Protect Your Deposit"
        cta1Link="/register"
        cta2Text="Find an Agent"
        cta2Link="/find-agent"
        hasGlow={true}
        gradientWord="back"
      />

      {/* Value tiles */}
      <SocialProofBar tiles={tiles} />

      {/* Features */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2
            className="font-display font-bold mb-16 text-center"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Everything on Your Side
          </h2>
          <FeatureCardGrid features={features} />
        </div>
      </section>

      {/* Why Orello */}
      <section style={{ background: "#111111", padding: "4rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                <p
                  className="text-xs tracking-widest uppercase"
                  style={{
                    background: "linear-gradient(135deg, #F97316, #A78BFA)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Why Orello
                </p>
              </div>
              <h3
                className="font-display font-bold mb-6"
                style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "#F5F5F5" }}
              >
                Peace of mind, guaranteed.
              </h3>
              <p
                className="text-base font-light leading-relaxed mb-8"
                style={{ color: "#D1D5DB", maxWidth: 500 }}
              >
                Deposits are where trust breaks down. Orello fixes that by holding your deposit securely — and giving you a platform to review every claim, dispute anything unfair, and get a binding resolution without stepping into a courtroom.
              </p>
              <Link
                to="/register"
                className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                  fontSize: "1rem",
                  boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
                }}
              >
                Get Started Free <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* How the deposit works */}
            <div style={{
              background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))",
              borderRadius: 20,
              padding: "2.5rem",
              border: "1px solid rgba(249,115,22,0.15)",
            }}>
              {[
                { step: "1", label: "Deposit paid to Orello, not the landlord", sub: "They can't touch it without your sign-off." },
                { step: "2", label: "Room-by-room move-in report locked", sub: "Photos, condition, notes — timestamped forever." },
                { step: "3", label: "You review every deduction at move-out", sub: "Accept, counter, or dispute — your choice." },
                { step: "4", label: "Dispute? ORE-AI decides instantly", sub: "Evidence-based. Binding. No courts needed." },
              ].map((s) => (
                <div key={s.step} style={{
                  display: "flex", alignItems: "flex-start", gap: 14,
                  paddingBottom: s.step !== "4" ? "1.25rem" : 0,
                  marginBottom: s.step !== "4" ? "1.25rem" : 0,
                  borderBottom: s.step !== "4" ? "1px solid rgba(245,245,245,0.07)" : "none",
                }}>
                  <div style={{
                    minWidth: 28, height: 28, borderRadius: "50%",
                    background: "#F97316", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
                  }}>{s.step}</div>
                  <div>
                    <p style={{ fontSize: "0.88rem", fontWeight: 600, color: "#F5F5F5", margin: 0 }}>{s.label}</p>
                    <p style={{ fontSize: "0.78rem", color: "#9CA3AF", margin: "2px 0 0" }}>{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#080808", padding: "4rem 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-display font-bold leading-tight mb-6"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Your next rental.<br />
            <span style={{
              background: "linear-gradient(135deg, #F97316, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              protected.
            </span>
          </h2>
          <p className="text-base font-light mb-12" style={{ color: "#D1D5DB" }}>
            No credit card. No setup fee. Ready in minutes.
          </p>
          <Link
            to="/register"
            className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              fontSize: "1.05rem",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
            }}
          >
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Find Your Agent — tenant card grid with rating focus */}
      <section style={{ background: "#0B0B14", padding: "5rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4">
            <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #10B981, #3B82F6)" }} />
            <p className="text-xs tracking-widest uppercase" style={{ color: "#10B981" }}>Find Your Agent</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display font-bold" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", color: "#F5F5F5", lineHeight: 1.2 }}>
                Top-rated agents<br />near you.
              </h2>
              <p className="mt-2" style={{ fontSize: "0.88rem", color: "#9CA3AF" }}>
                Every agent is Orello-verified. Rated by real tenants after every deal.
              </p>
            </div>
          </div>

          {/* Search bar */}
          <div className="relative mb-8">
            <Search size={16} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#10B981" }} />
            <input
              type="text"
              placeholder="Search your city — Bangalore, Mumbai, Delhi…"
              value={citySearch}
              onChange={e => setCitySearch(e.target.value)}
              onFocus={() => setShowCities(true)}
              onBlur={() => setTimeout(() => setShowCities(false), 150)}
              style={{
                width: "100%", padding: "13px 16px 13px 44px",
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(16,185,129,0.25)",
                borderRadius: 12, color: "#F5F5F5", fontSize: "0.9rem", outline: "none",
              }}
            />
            {showCities && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                background: "#111", border: "1px solid rgba(16,185,129,0.15)",
                borderRadius: 10, zIndex: 10, overflow: "hidden",
              }}>
                {CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).map(c => (
                  <button key={c} onMouseDown={() => { setCity(c); setCitySearch(c); setShowCities(false); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "10px 16px", fontSize: "0.88rem",
                      color: c === city ? "#10B981" : "#D1D5DB",
                      background: c === city ? "rgba(16,185,129,0.08)" : "transparent",
                      border: "none", cursor: "pointer",
                    }}>{c}</button>
                ))}
              </div>
            )}
          </div>

          {/* Agent cards — tenant-focused: rating + tenant count + 0 disputes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CITY_AGENTS[city].map((agent, i) => (
              <div key={agent.rank} style={{
                background: i === 0 ? "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.08))" : "rgba(255,255,255,0.02)",
                border: `1px solid ${i === 0 ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.06)"}`,
                borderRadius: 14, padding: "20px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%",
                    background: i === 0 ? "linear-gradient(135deg, #10B981, #3B82F6)" : "rgba(255,255,255,0.07)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 700, fontSize: "0.82rem", flexShrink: 0,
                  }}>{initials(agent.name)}</div>
                  <div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#F5F5F5" }}>{agent.name}</div>
                    <div style={{ fontSize: "0.75rem", color: "#6B7280" }}>{agent.area}, {city}</div>
                  </div>
                  {i === 0 && <span style={{ marginLeft: "auto", fontSize: "0.65rem", fontWeight: 700, background: "rgba(16,185,129,0.15)", color: "#10B981", padding: "3px 8px", borderRadius: 4 }}>Top Pick</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Rating", val: `★ ${agent.rating}`, col: "#F97316" },
                    { label: "Deals", val: agent.deals, col: "#F5F5F5" },
                    { label: "Disputes", val: agent.disputes === 0 ? "None" : agent.disputes, col: agent.disputes === 0 ? "#10B981" : "#EF4444" },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: "center", background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 4px" }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 700, color: s.col }}>{s.val}</div>
                      <div style={{ fontSize: "0.65rem", color: "#6B7280", marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: "0.65rem", fontWeight: 600, background: "rgba(16,185,129,0.1)", color: "#10B981", padding: "3px 8px", borderRadius: 4, border: "1px solid rgba(16,185,129,0.2)" }}>✓ Orello Verified</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/register"
              className="btn-press inline-flex items-center gap-2 font-medium px-7 py-3.5 rounded-full text-white"
              style={{ background: "linear-gradient(135deg, #10B981 0%, #3B82F6 100%)", fontSize: "0.95rem" }}>
              Sign Up & Find Your Agent <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
